import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  Avatar,
  Fade,
  Zoom,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Get, Post, Put, apiURL } from '../../../utils/apiCalls';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

const ProcurementApproval = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [procurementData, setProcurementData] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [approvalComments, setApprovalComments] = useState('');

  // Fetch procurement data
  const fetchProcurementData = async () => {
    setLoading(true);
    try {
      const response = await Get(apiURL + `/api/v1/procurement-admin-users/${id}`,'');
      if (response && response.data && response.data.status) {
        setProcurementData(response.data.data);
      } else {
        toast.error(response?.data?.message || 'Failed to fetch procurement data');
        navigate('/dashboard/procurement');
      }
    } catch (error) {
      console.error('Error fetching procurement data:', error);
      toast.error('An error occurred while fetching procurement data');
      navigate('/dashboard/procurement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProcurementData();
    }
  }, [id]);

  const handleApprovalAction = (action) => {
    setApprovalAction(action);
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalAction) {
      toast.error('Please select an approval action');
      return;
    }

    if (approvalAction === 'Rejected' && !approvalReason) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setSaving(true);
    try {
      const payload = {
    status: approvalAction?.toUpperCase(),
      };
      const response = await Put(apiURL + `/api/v1/procurement-admin-users/`, id, payload);
      if (response && response.data && response.data.status) {
        toast.success(`Procurement ${approvalAction === 'active' ? 'approved' : 'rejected'} successfully!`);
        navigate('/dashboard/procurement');
      } else {
        toast.error(response?.data?.message || `Failed to ${approvalAction === 'active' ? 'approve' : 'reject'} procurement`);
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('An error occurred while processing the approval');
    } finally {
      setSaving(false);
      setApprovalDialogOpen(false);
    }
  };

  const getKYCStatusColor = (verified) => {
    return verified ? 'success' : 'error';
  };

  const getKYCStatusText = (verified) => {
    return verified ? 'Verified' : 'Not Verified';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!procurementData) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Alert severity="error">
          <Typography variant="h6">Procurement data not found</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{
              bgcolor: '#1976d2',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}>
              <CheckCircleIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                Procurement Approval
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Review and approve/reject procurement registration
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      {/* Status Alert */}
      <Fade in timeout={1000}>
        <Alert
          severity={procurementData.status?.toLowerCase() === 'active' ? 'success' :
                    procurementData.status?.toLowerCase()  === 'rejected' ? 'error' : 'warning'}
          sx={{ mb: 3, borderRadius: 2 }}
          icon={procurementData.status?.toLowerCase()  === 'active' ? <CheckCircleIcon /> :
                procurementData.status?.toLowerCase()  === 'rejected' ? <CancelIcon /> : <WarningIcon />}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Current Status: {procurementData.status?.toUpperCase() || 'PENDING'}
          </Typography>
          <Typography variant="body2">
            {procurementData.status?.toLowerCase()  === 'active' ? 'This procurement has been approved.' :
             procurementData.status?.toLowerCase()  === 'rejected' ? 'This procurement has been rejected.' :
             'This procurement is pending approval.'}
          </Typography>
        </Alert>
      </Fade>

      <Grid container spacing={3}>
        {/* Left Column - Procurement Details */}
        <Grid item xs={12} md={8}>
          <Zoom in timeout={1200}>
            <Card elevation={4} sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }}>
              <CardContent sx={{ p: 4 }}>
                {/* Personal Information */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa', mb: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Personal Information
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Name:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {procurementData.first_name} {procurementData.last_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Email:</Typography>
                      <Typography variant="body1">{procurementData.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Phone:</Typography>
                      <Typography variant="body1">{procurementData.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Role:</Typography>
                      <Typography variant="body1">{procurementData.role?.designation || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Aadhaar:</Typography>
                      <Typography variant="body1">{procurementData.aadhar_number}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>PAN:</Typography>
                      <Typography variant="body1">{procurementData.pan_number}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Address:</Typography>
                      <Typography variant="body1">{procurementData.address}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>City:</Typography>
                      <Typography variant="body1">{procurementData.city}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>State:</Typography>
                      <Typography variant="body1">{procurementData.state}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Pincode:</Typography>
                      <Typography variant="body1">{procurementData.pincode}</Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Bank Information */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Bank Information
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Bank Name:</Typography>
                      <Typography variant="body1">{procurementData.bank_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Account Number:</Typography>
                      <Typography variant="body1">{procurementData.bank_account_number}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>IFSC Code:</Typography>
                      <Typography variant="body1">{procurementData.bank_ifsc_code}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Account Holder:</Typography>
                      <Typography variant="body1">{procurementData.bank_account_holder_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Bank Address:</Typography>
                      <Typography variant="body1">{procurementData.bank_address}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Right Column - KYC Status & Actions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* KYC Status Card */}
            <Zoom in timeout={1400}>
              <Card elevation={4} sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    KYC Verification Status
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">PAN Verification</Typography>
                      <Chip
                        label={getKYCStatusText(procurementData.pan_number)}
                        color={getKYCStatusColor(procurementData.pan_number)}
                        size="small"
                        icon={procurementData.pan_number ? <CheckCircleIcon /> : null}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Aadhaar Verification</Typography>
                      <Chip
                          label={getKYCStatusText(procurementData.aadhar_number)}
                        color={getKYCStatusColor(procurementData.aadhar_number)}
                        size="small"
                        icon={procurementData.aadhar_number ? <CheckCircleIcon /> : null}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Bank Verification</Typography>
                      <Chip
                        label={getKYCStatusText(procurementData.bank_account_number )}
                        color={getKYCStatusColor(procurementData.bank_account_number)}
                        size="small"
                        icon={procurementData.bank_account_number ? <CheckCircleIcon /> : null}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>

            {/* Registration Details Card */}
            <Zoom in timeout={1600}>
              <Card elevation={4} sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    Registration Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Status:</Typography>
                      <Chip
                        label={procurementData.status || 'Pending'}
                        color={getStatusColor(procurementData.status)}
                        variant="filled"
                        size="small"
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Registered On:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(procurementData.created_at)}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>

            {/* Action Buttons */}
            {procurementData.status?.toLowerCase() !== 'active' && procurementData.status !== 'Rejected' && (
              <Zoom in timeout={1800}>
                <Card elevation={4} sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Approval Actions
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUpIcon />}
                        onClick={() => handleApprovalAction('active')}
                        fullWidth
                        sx={{ py: 1.5, fontWeight: 'bold' }}
                      >
                        Approve Registration
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<ThumbDownIcon />}
                        onClick={() => handleApprovalAction('Rejected')}
                        fullWidth
                        sx={{ py: 1.5, fontWeight: 'bold' }}
                      >
                        Reject Registration
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/dashboard/procurement')}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        Back to List
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Zoom>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{
              bgcolor: approvalAction === 'active' ? '#4caf50' : '#f44336',
              width: 40,
              height: 40
            }}>
              {approvalAction === 'active' ? <ThumbUpIcon /> : <ThumbDownIcon />}
            </Avatar>
            <Typography variant="h6" component="h2" fontWeight="bold">
              {approvalAction === 'active' ? 'Approve' : 'Reject'} Procurement Registration
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {approvalAction === 'Rejected' && (
              <FormControl fullWidth>
                <InputLabel>Reason for Rejection</InputLabel>
                <Select
                  value={approvalReason}
                  onChange={(e) => setApprovalReason(e.target.value)}
                  label="Reason for Rejection"
                >
                  <MenuItem value="incomplete_documents">Incomplete Documents</MenuItem>
                  <MenuItem value="invalid_information">Invalid Information</MenuItem>
                  <MenuItem value="kyc_failed">KYC Verification Failed</MenuItem>
                  <MenuItem value="duplicate_registration">Duplicate Registration</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            )}
            
            <TextField
              fullWidth
              label="Comments (Optional)"
              multiline
              rows={4}
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              placeholder="Add any additional comments or notes..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={approvalAction === 'active' ? 'success' : 'error'}
            onClick={handleApprovalSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Processing...' : `${approvalAction === 'active' ? 'Approve' : 'Reject'} Registration`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProcurementApproval;
